import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import {
  BodyanalyticsBaseComponent,
  SessionStorageService,
  BrandLogoComponent,
  ThemeService,
} from '@bodyanalytics/ui-core';
import { LoginService } from '../../services/login.service';
import { Router, RouterLink } from '@angular/router';
import { RouterUrlsEnum } from '@bodyanalytics/ui-core';
import { tap } from 'rxjs';
import { ENV, marketingLink } from '@bodyanalytics/app-config';
import { LoginI } from '@bodyanalytics/data-models-ui';
import { HttpErrorResponse } from '@angular/common/http';
import { EmbeddedLoginFormComponent } from '../../components/embedded-login-form/embedded-login-form.component';
import { EmbeddedSessionService } from '../../embedded/embedded-session.service';

/** Time-of-day greeting for the admin/staff panel — a small warm touch, not tied
 *  to any identity (nobody's signed in yet). */
function staffGreetingForHour(hour: number): 'morning' | 'afternoon' | 'evening' {
  if (hour < 12) {
    return 'morning';
  }
  if (hour < 18) {
    return 'afternoon';
  }
  return 'evening';
}

/**
 * Shared sign-in page, redesigned to the "Portal — Sign in (Quiet Authority)"
 * Paper artboard (split screen: form column + dark report panel).
 *
 * Two sign-in paths, chosen by `embeddedLoginEnabled`:
 * - ON: in-app email/password via EmbeddedSessionService → auth-session-api
 *   (ROPG proxy). A backend `embedded_login_disabled` (503) answer degrades to
 *   the redirect path automatically, so a stale bundle can't strand visitors.
 * - OFF (default): single CTA → `auth.loginWithRedirect()` — behaviour and
 *   security posture identical to the pre-redesign page.
 */
@Component({
  selector: 'bodyanalytics-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, BrandLogoComponent, EmbeddedLoginFormComponent],
})
export class LoginComponent extends BodyanalyticsBaseComponent implements OnInit {
  private sessionSessionService = inject(SessionStorageService);
  auth = inject(AuthService);
  protected readonly theme = inject(ThemeService);
  private loginService = inject(LoginService);
  private embeddedSession = inject(EmbeddedSessionService);
  private router = inject(Router);
  private appConfig = inject(ENV);

  readonly staffGreeting = staffGreetingForHour(new Date().getHours());
  readonly currentYear = new Date().getFullYear();

  readonly privacyUrl = marketingLink(this.appConfig, 'privacy');
  readonly termsUrl = marketingLink(this.appConfig, 'terms');
  readonly helpUrl = marketingLink(this.appConfig, 'contact');

  /** Embedded fields render only when the build-time flag is on. */
  readonly embeddedEnabled = this.appConfig.embeddedLoginEnabled === true;

  readonly signInPending = signal(false);
  readonly signInError = signal<string | null>(null);

  returnUrl = '';

  ngOnInit(): void {
    const isCustomerPortal = this.appConfig.portalName === 'customer';
    const bookingData = this.sessionSessionService.getBookingData();
    const workflowStep = bookingData?.workflowStep ?? 'search';
    const eventId = bookingData?.selectedEvent?.id;

    switch (workflowStep) {
      case 'checkout':
        this.returnUrl = `${RouterUrlsEnum.CALENDER_CHECKOUT}/${eventId}`;
        break;
      case 'signup-verify':
        this.returnUrl = `${RouterUrlsEnum.CALENDAR_SIGNUP_VERIFY}/${eventId}`;
        break;
      case 'signup':
        // #1933 — a user mid-signup (workflowStep === 'signup') must return to
        // THEIR event's signup form after login, not fall through to the generic
        // search page. (CALENDER_SIGNUP has no trailing slash, so add one.)
        this.returnUrl = `${RouterUrlsEnum.CALENDER_SIGNUP}/${eventId}`;
        break;
      case 'search':
        this.returnUrl = `${RouterUrlsEnum.CALENDER_EVENTS_SEARCH}`;
        break;
      default:
        this.returnUrl = `${RouterUrlsEnum.CALENDER_EVENTS_SEARCH}`; // Define a default URL if needed
        break;
    }

    // Customer portal (#840): there is no calendar booking funnel in this app, so a
    // signed-in member with no active booking workflow should land on their member
    // dashboard rather than the calendar search route (which isn't mounted here).
    if (isCustomerPortal && workflowStep === 'search') {
      this.returnUrl = RouterUrlsEnum.AUTH_DASHBOARD;
    }

    // Admin portal (#2119): the staff portal has no booking funnel at all; a
    // signed-in admin always lands on the admin dashboard.
    if (this.isAdminPortal()) {
      this.returnUrl = RouterUrlsEnum.AUTH_ADMIN_DASHBOARD;
    }

    // The bridged AuthService (embedded login) also reports an active embedded
    // session here, so a signed-in visitor of either kind is forwarded on.
    this.subscriptions.barrel = this.auth.isAuthenticated$
      .pipe(
        tap(loggedIn => {
          if (loggedIn) {
            this.router.navigateByUrl(this.returnUrl);
          }
          // Signed-out: ALWAYS stay on /login, in every portal, so the visitor can
          // trigger Auth0 (#840 customer, #2119 admin) — see the git history of
          // this comment for the calendar bounce regression (#2126/#2132).
        }),
      )
      .subscribe();
  }

  onCredentialsSubmit(credentials: LoginI): void {
    if (this.signInPending()) {
      return;
    }
    this.signInPending.set(true);
    this.signInError.set(null);

    this.subscriptions.barrel = this.embeddedSession
      .login(credentials.emailAddress, credentials.password ?? '')
      .subscribe({
        next: () => {
          this.signInPending.set(false);
          this.router.navigateByUrl(this.returnUrl);
        },
        error: (error: HttpErrorResponse) => {
          this.signInPending.set(false);
          if (error.error?.code === 'embedded_login_disabled' || error.status === 503) {
            // Backend gate closed (flag drift or Auth0 grant not enabled yet):
            // degrade to the redirect flow instead of dead-ending the visitor.
            this.onRedirectLogin(credentials.emailAddress);
            return;
          }
          this.signInError.set(
            error.status === 401
              ? 'That email and password don’t match our records. Check them and try again.'
              : 'Sign-in is temporarily unavailable. Try again in a moment.',
          );
        },
      });
  }

  onRedirectLogin(emailHint?: string): void {
    this.subscriptions.barrel = this.auth
      .loginWithRedirect(
        emailHint
          ? { authorizationParams: { login_hint: emailHint }, appState: { target: this.returnUrl } }
          : { appState: { target: this.returnUrl } },
      )
      .subscribe();
  }

  onForgotPassword(emailHint: string): void {
    // Password reset lives on the Auth0 hosted page — hand off with the typed
    // email prefilled so the visitor only has to click "Forgot password" there.
    this.onRedirectLogin(emailHint || undefined);
  }

  signup() {
    this.loginService.setReturnUrl(this.returnUrl);
    this.router.navigate([`${RouterUrlsEnum.AUTH_REGISTER_ACCOUNT}`]);
  }

  public isAdminPortal(): boolean {
    return this.appConfig.portalName === 'admin';
  }
}
